import { useEffect, useState } from 'react';
import PixTab from '../../checkout/PixTab';
import CartaoTab from '../../checkout/CartaoTab';
import BoletoTab from '../../checkout/BoletoTab';
import type { Aba } from '../types';

type Props = {
  open: boolean;
  onClose: () => void;
  slug: string;
  selectionString: string;
  valor: number;
  cupomCodigo: string | null;
  nomeHomenageado: string;
  onApproved: () => void;
};

export default function PaymentBottomSheet({
  open, onClose, slug, selectionString, valor, cupomCodigo, nomeHomenageado, onApproved,
}: Props) {
  const [aba, setAba] = useState<Aba>('pix');

  // Bloqueia scroll do body quando aberto + fecha com ESC
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  return (
    <>
      <div
        className={`mz-bs-overlay ${open ? 'mz-bs-open' : ''}`}
        onClick={onClose}
        aria-hidden
      />
      <div
        className={`mz-bs ${open ? 'mz-bs-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Forma de pagamento"
      >
        <button type="button" className="mz-bs-handle-wrap" onClick={onClose} aria-label="Fechar">
          <span className="mz-bs-handle" aria-hidden />
        </button>

        <header className="mz-bs-head">
          <div className="mz-bs-eyebrow">Pagamento seguro · Asaas</div>
          <h3 className="mz-bs-title">Como você quer pagar?</h3>
          <p className="mz-bs-sub">Assim que confirmar, mandamos a música pra <em>{nomeHomenageado}</em> no WhatsApp.</p>
        </header>

        <div role="tablist" aria-label="Forma de pagamento" className="mz-bs-tabs">
          <button
            type="button" role="tab" aria-selected={aba === 'pix'}
            onClick={() => setAba('pix')}
            className={`mz-bs-tab ${aba === 'pix' ? 'mz-bs-tab-active' : ''}`}
          >
            <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden>
              <path d="m11.917 11.71-2.314 2.314a.974.974 0 0 1-1.378 0L5.91 11.71a2.16 2.16 0 0 1 0-3.054l2.314-2.314c.378-.378.999-.378 1.378 0l2.314 2.314a2.16 2.16 0 0 1 0 3.054Zm9.166-3.054L18.77 6.342a.974.974 0 0 0-1.378 0L15.078 8.656a2.16 2.16 0 0 0 0 3.054l2.314 2.314a.974.974 0 0 0 1.378 0l2.313-2.314a2.16 2.16 0 0 0 0-3.054Zm-7.252-5.18-2.314-2.314a.974.974 0 0 0-1.378 0L7.825 3.476a2.16 2.16 0 0 0 0 3.054l2.314 2.314a.974.974 0 0 0 1.378 0l2.314-2.314a2.16 2.16 0 0 0 0-3.054Zm0 17.048-2.314 2.314a.974.974 0 0 1-1.378 0L7.825 20.524a2.16 2.16 0 0 1 0-3.054l2.314-2.314a.974.974 0 0 1 1.378 0l2.314 2.314a2.16 2.16 0 0 1 0 3.054Z"/>
            </svg>
            <div>
              <div className="mz-bs-tab-title">PIX</div>
              <div className="mz-bs-tab-hint">na hora</div>
            </div>
          </button>
          <button
            type="button" role="tab" aria-selected={aba === 'cartao'}
            onClick={() => setAba('cartao')}
            className={`mz-bs-tab ${aba === 'cartao' ? 'mz-bs-tab-active' : ''}`}
          >
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <rect x="2" y="5" width="20" height="14" rx="3" />
              <path d="M2 10h20" />
              <path d="M6 15h4" />
            </svg>
            <div>
              <div className="mz-bs-tab-title">Cartão</div>
              <div className="mz-bs-tab-hint">crédito/débito</div>
            </div>
          </button>
          <button
            type="button" role="tab" aria-selected={aba === 'boleto'}
            onClick={() => setAba('boleto')}
            className={`mz-bs-tab ${aba === 'boleto' ? 'mz-bs-tab-active' : ''}`}
          >
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M3 4v16" />
              <path d="M7 4v16" />
              <path d="M11 4v16" />
              <path d="M15 4v16" />
              <path d="M19 4v16" />
            </svg>
            <div>
              <div className="mz-bs-tab-title">Boleto</div>
              <div className="mz-bs-tab-hint">1–2 dias</div>
            </div>
          </button>
        </div>

        <div className="mz-bs-content">
          {aba === 'pix' && (
            <PixTab
              slug={slug}
              selectionString={selectionString}
              valor={valor}
              cupomCodigo={cupomCodigo}
            />
          )}
          {aba === 'cartao' && (
            <CartaoTab
              slug={slug}
              selectionString={selectionString}
              valor={valor}
              nomeSugerido={nomeHomenageado}
              cupomCodigo={cupomCodigo}
              onApproved={onApproved}
            />
          )}
          {aba === 'boleto' && (
            <BoletoTab
              slug={slug}
              selectionString={selectionString}
              nomeSugerido={nomeHomenageado}
              cupomCodigo={cupomCodigo}
            />
          )}
        </div>

        <div className="mz-bs-foot">
          Pagamento processado pela Asaas, autorizada pelo Banco Central · Não armazenamos dados de cartão.
        </div>

        <style>{`
          .mz-bs-overlay {
            position: fixed;
            inset: 0;
            background: rgba(10, 5, 0, 0.46);
            backdrop-filter: blur(3px);
            -webkit-backdrop-filter: blur(3px);
            z-index: 90;
            opacity: 0;
            pointer-events: none;
            transition: opacity 0.32s ease;
          }
          .mz-bs-overlay.mz-bs-open {
            opacity: 1;
            pointer-events: auto;
          }

          .mz-bs {
            position: fixed;
            left: 0; right: 0; bottom: 0;
            z-index: 100;
            max-height: 92vh;
            display: flex;
            flex-direction: column;
            background: var(--surface-white);
            border-top-left-radius: 24px;
            border-top-right-radius: 24px;
            box-shadow: 0 -22px 56px -16px rgba(80,40,10,0.32);
            transform: translateY(102%);
            transition: transform 0.36s cubic-bezier(0.22, 1, 0.36, 1);
            padding-bottom: env(safe-area-inset-bottom);
            overflow: hidden;
          }
          .mz-bs.mz-bs-open { transform: translateY(0); }

          html[data-theme='dark'] .mz-bs {
            background: #1c1226;
            border-top: 1px solid rgba(255,145,77,0.20);
          }

          .mz-bs-handle-wrap {
            display: flex;
            justify-content: center;
            padding: 12px 0 8px;
            background: transparent;
            border: none;
            cursor: pointer;
            width: 100%;
          }
          .mz-bs-handle {
            width: 44px;
            height: 4px;
            border-radius: 999px;
            background: var(--border-soft);
          }

          .mz-bs-head {
            padding: 4px 22px 16px;
          }
          .mz-bs-eyebrow {
            font-family: 'DM Sans', sans-serif;
            font-size: 10.5px;
            font-weight: 700;
            letter-spacing: 0.18em;
            text-transform: uppercase;
            color: var(--amber-deep);
            margin-bottom: 6px;
          }
          .mz-bs-title {
            font-family: 'Playfair Display', 'Source Serif 4', serif;
            font-style: italic;
            font-weight: 700;
            font-size: 26px;
            line-height: 1.1;
            letter-spacing: -0.02em;
            color: var(--heading-ink);
            margin: 0 0 8px;
          }
          .mz-bs-sub {
            font-size: 13.5px;
            color: var(--text-muted);
            margin: 0;
            line-height: 1.45;
          }
          .mz-bs-sub em {
            font-style: italic;
            font-weight: 600;
            color: var(--gold);
            font-family: 'Playfair Display', serif;
          }

          .mz-bs-tabs {
            display: grid;
            grid-template-columns: 1.15fr 1fr 1fr;
            gap: 8px;
            padding: 0 22px 14px;
          }
          .mz-bs-tab {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 6px;
            padding: 14px 12px;
            background: var(--warm-white);
            border: 1.5px solid var(--border-soft);
            border-radius: 14px;
            cursor: pointer;
            color: var(--text-muted);
            font-family: 'DM Sans', sans-serif;
            transition: all 0.18s;
            min-height: 70px;
          }
          .mz-bs-tab:hover { color: var(--amber-deep); border-color: var(--gold-border); }
          .mz-bs-tab:focus-visible {
            outline: 3px solid var(--gold-pale);
            outline-offset: 2px;
          }

          .mz-bs-tab-active {
            background: linear-gradient(135deg, var(--gold) 0%, var(--gold-hover) 100%);
            border-color: var(--gold);
            color: #fff;
            box-shadow: 0 6px 16px rgba(255,145,77,0.42);
          }
          .mz-bs-tab-title {
            font-size: 14.5px;
            font-weight: 800;
            letter-spacing: -0.01em;
          }
          .mz-bs-tab-hint {
            font-size: 10.5px;
            font-weight: 600;
            opacity: 0.78;
            letter-spacing: 0.04em;
          }

          .mz-bs-content {
            flex: 1;
            overflow-y: auto;
            padding: 4px 22px 14px;
            -webkit-overflow-scrolling: touch;
          }

          .mz-bs-foot {
            text-align: center;
            font-family: 'DM Sans', sans-serif;
            font-size: 10.5px;
            color: var(--text-soft);
            padding: 10px 22px 16px;
            line-height: 1.5;
            border-top: 1px solid var(--border-fade);
          }

          @media (min-width: 720px) {
            .mz-bs {
              left: 50%;
              right: auto;
              transform: translate(-50%, 102%);
              width: 680px;
              max-width: 96%;
              border-radius: 24px;
              bottom: 4vh;
            }
            .mz-bs.mz-bs-open { transform: translate(-50%, 0); }
          }
        `}</style>
      </div>
    </>
  );
}
